// frontend/src/widgets/OpenConversationsWidget.jsx
// Project:      Vision ANPR by Castle Traffic Ltd - Automatic Number Plate Recognition
// Author:       Leon du Plooy - Castle Traffic Ltd - Copyright 2025
// Contact:      dev@castle-traffic.co.uk
// Created:      2025-12-16
// Updated:      2026-09-08 16:21 BST
//
// Description:
//   Open Conversations widget.
//   • Will list open conversations across all channels.
//   • Future: filters by channel, age, priority, job/contact,
//     plus click-through to full conversation thread.
//
// Lifecycle Status:
//   Active stub.
//
// Mobile Responsiveness (2026-09-08):
//   - Preserves one render path for desktop and mobile.
//   - Stacks the heading/description and channel selector on narrow screens.
//   - Makes the channel selector full-width and touch-friendly on mobile.
//   - Preserves the desktop conversation table.
//   - Hides the empty desktop table header on mobile and presents the current
//     stub message as a simple bordered card.
//   - Uses natural height on mobile while retaining desktop fill behaviour.
//   - Allows all explanatory text to wrap safely without horizontal overflow.
//
// Operational Notes:
//   - Live conversation data wiring is not implemented.
//   - The channel selector remains presentation-only until the widget is wired.
//   - No API calls, polling, navigation or conversation actions are introduced.

import React from "react";

import { useTheme } from "../context/ThemeContext.jsx";

const OpenConversationsWidget = () => {
  const { theme } = useTheme() || {};

  const accent =
    theme?.accent_color ||
    "#ff7a00";

  return (
    <div className="flex h-auto min-h-0 w-full flex-col overflow-visible md:h-full md:overflow-hidden">
      {/* Header */}
      <div className="mb-3 flex flex-col gap-3 md:flex-row md:items-start md:justify-between">
        <div className="min-w-0 flex-1">
          <h3
            className="m-0 break-words text-base font-semibold"
            style={{
              color: accent,
              overflowWrap:
                "anywhere",
            }}
          >
            Open Conversations
          </h3>

          <p
            className="mt-1 mb-0 break-words text-xs leading-5 text-gray-600"
            style={{
              overflowWrap:
                "anywhere",
            }}
          >
            High-level view of all active conversations.
          </p>
        </div>

        <div className="w-full shrink-0 md:w-auto">
          <label
            htmlFor="open-conversations-channel"
            className="sr-only"
          >
            Conversation channel
          </label>

          <select
            id="open-conversations-channel"
            className="min-h-[44px] w-full rounded border border-gray-300 bg-white px-3 py-2 text-sm text-gray-700 focus:outline-none focus:ring-2 md:min-h-0 md:w-auto md:px-2 md:py-1 md:text-xs"
            style={{
              "--tw-ring-color":
                accent,
            }}
            defaultValue="all"
          >
            <option value="all">
              All channels
            </option>

            <option value="email">
              Email
            </option>

            <option value="sms">
              SMS
            </option>

            <option value="whatsapp">
              WhatsApp
            </option>
          </select>
        </div>
      </div>

      {/* Body */}
      <div className="min-h-0 flex-1 overflow-visible rounded-lg border border-dashed border-gray-300 bg-gray-50 text-xs md:overflow-auto">
        <table className="block w-full text-left text-gray-700 md:table">
          <thead className="hidden md:table-header-group">
            <tr className="bg-white">
              <th className="border-b px-3 py-2">
                Channel
              </th>

              <th className="border-b px-3 py-2">
                Contact / Job
              </th>

              <th className="border-b px-3 py-2">
                Subject
              </th>

              <th className="whitespace-nowrap border-b px-3 py-2">
                Last activity
              </th>

              <th className="border-b px-3 py-2">
                Status
              </th>
            </tr>
          </thead>

          <tbody className="block p-3 md:table-row-group md:p-0">
            <tr className="block rounded-lg border border-gray-200 bg-white md:table-row md:rounded-none md:border-0 md:bg-gray-50">
              <td
                className="block break-words px-3 py-3 text-gray-500 md:table-cell md:py-2"
                colSpan={5}
                style={{
                  overflowWrap:
                    "anywhere",
                }}
              >
                Live data wiring not implemented yet. This is a safe stub for
                layout, registration and styling.
              </td>
            </tr>
          </tbody>
        </table>
      </div>
    </div>
  );
};

export default OpenConversationsWidget;
